/**
 * HartIndexPage - Main page for viewing Hart data across 5 entity types
 * (jobs, analysts, vendors, clients, facilities).
 * Handles data fetching, caching, pagination, and coordinates child components.
 */
import { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useParams, Link } from "react-router-dom";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import buildRoute from "components/buildRoute";
import PageHeader from "components/PageHeader";
import InlineMessage from "components/InlineMessage";
import BrowserTimezoneAlert from "components/BrowserTimezoneAlert";
import { canContribute, canEdit, canAdminister, deleteRecord, formatDateOnly, formatDateRange, normalizeDateString } from "./hartUtils";
import HartTable from "./components/HartTable";
import HartSearchFilters from "./components/HartSearchFilters";
import getCurrentUser from "hooks/getCurrentUser";
import { getCsrfToken } from "utils/csrf";
import { HART_TABLES_CONFIG, HART_TABLES_SORT_CONFIG } from "./hartTablesConfig";
import { trackEvent, trackEngagementActivity } from "../../configureMixpanel";

// Constants
const SERVER_PAGE_SIZE = 1000;
const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_TABLE_VIEW = 'jobs';
const ROWS_PER_PAGE_STORAGE_KEY = 'hart_rows_per_page';
const CURRENT_TABLE_VIEW_STORAGE_KEY = 'hart_current_table_view';

// Header actions component
const HeaderActions = ({ currentTable, setCurrentTable, showAddButton }) => (
  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      Currently showing
    </Typography>
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <InputLabel>View</InputLabel>
      <Select
        value={currentTable}
        label="View"
        onChange={(e) => setCurrentTable(e.target.value)}
      >
        <MenuItem value="jobs">Jobs</MenuItem>
        <MenuItem value="analysts">Analysts</MenuItem>
        <MenuItem value="vendors">Vendors</MenuItem>
        <MenuItem value="clients">Clients</MenuItem>
        <MenuItem value="facilities">Facilities</MenuItem>
      </Select>
    </FormControl>
    {showAddButton && (
      <Button
        component={Link}
        to="new"
        variant="contained"
        color="primary"
        aria-label="Add New"
        sx={{ '&:hover': { backgroundColor: 'secondary.main' } }}
      >
        Add New
      </Button>
    )}
  </Box>
);

/**
 * Hart Index Page - Jobs List with Smart Pagination
 * 
 * DATA FETCHING STRATEGY:
 * - Without filters: Fetches 1000 records at a time from server (fast initial load)
 * - With filters: Fetches ALL matching records once, then paginates client-side
 * 
 * Why this approach?
 * - Backend only supports status filtering (show_all), not complex filters
 * - Complex filters (10+ fields including date ranges, text search) easier client-side
 * - Dataset size (~12K records) manageable for client-side filtering
 * 
 * PERFORMANCE OPTIMIZATIONS:
 * - 500ms debounce on search input (prevents query on every keystroke)
 * - AbortController cancels in-flight requests when new search starts
 */
export const HartIndexPage = () => {
  const [hartData, setHartData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentTable, setCurrentTable] = useState(() => {
    const saved = localStorage.getItem(CURRENT_TABLE_VIEW_STORAGE_KEY);
    return saved || DEFAULT_TABLE_VIEW;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isDataReady, setIsDataReady] = useState(false);
  const [hasReceivedData, setHasReceivedData] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const { projectId } = useParams();
  const [isFilteredDataset, setIsFilteredDataset] = useState(false);
  const [currentServerPage, setCurrentServerPage] = useState(null);
  const abortControllerRef = useRef(null);

  // Pagination state (server-side pagination)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem(ROWS_PER_PAGE_STORAGE_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_ROWS_PER_PAGE;
  });

  const [query, setQuery] = useState({
    search: null,
    filters: [],
    toggleFilter: null,
    jobsToggleFilter: null,
  });
  const lastFetchedQueryRef = useRef(null);
  const dataReceivedForTableRef = useRef(null);

  // Cache for all data (including non-active items)
  const allDataCacheRef = useRef({
    table: null,
    data: null
  });

  const hasSearchOrAdvancedFilters = query.search || query.filters.length > 0;
  const includeInactiveJobs = query.jobsToggleFilter?.value ?? false;

  // Apply toggle filter (status) to data array
  const applyToggleFilter = (data, toggleFilter, tableType) => {
    // Helper to get status name from either object or string format
    const getStatusName = (status) => {
      if (!status) return null;
      // Handle object format (ForeignKey): {name: "Active"}
      if (typeof status === 'object' && status.name) return status.name;
      // Handle string format: "Active"
      return String(status);
    };

    if (toggleFilter) {
      // If toggleFilter.value is true, include all items (no filtering)
      if (toggleFilter.value) return data;
      // Otherwise, only show Active items
      return data.filter(row => {
        const statusName = getStatusName(row.status);
        if (!statusName) return true;
        return statusName.toLowerCase() === 'active';
      });
    }
    // No toggleFilter in query - apply default filter (show only Active items)
    if (['jobs', 'vendors', 'clients', 'facilities'].includes(tableType)) {
      return data.filter(row => {
        const statusName = getStatusName(row.status);
        if (!statusName) return true;
        return statusName.toLowerCase() === 'active';
      });
    }
    return data;
  };

  // Map frontend table names to backend endpoint names
  const getBackendEndpoint = (tableType) => {
    const endpointMap = {
      'jobs': 'jobs',
      'analysts': 'analysts',
      'vendors': 'vendors',
      'clients': 'clients',
      'facilities': 'facilities',
    };
    return endpointMap[tableType];
  };

  const fetchHartData = async (forceServerPage = null) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsLoading(true);
    setIsDataReady(false);
    setHasReceivedData(false);
    setError(null);

    // Step 1: Check if endpoint exists
    const backendEndpoint = getBackendEndpoint(currentTable);
    if (!backendEndpoint) {
      setError(new Error(`Endpoint not available for ${currentTable}. Backend support coming soon.`));
      setHasReceivedData(true);
      setIsLoading(false);
      setIsDataReady(true);
      return;
    }

    if (hasSearchOrAdvancedFilters) {
      // Only clear data when filters are active (need to fetch all data)
      setHartData([]);
      setFilteredData([]);
      setIsFilteredDataset(false);
    }

    try {
      // Build base endpoint - construct the appropriate show_all parameter based on entity type
      // By default, backend filters to show only active records. Pass show_all=true to include all statuses.
      let showAllParam = '';
      if (query.toggleFilter && query.toggleFilter.value) {
        // If "Include All" toggle is ON, show all records including inactive
        if (['jobs', 'vendors', 'clients', 'facilities'].includes(currentTable)) {
          showAllParam = '&show_all=true';
        }
      }

      // For "Include All Jobs", serializers use `show_all_jobs` to decide whether
      // to include inactive jobs inside nested job/session lists.
      let showAllJobsParam = '';
      if (
        query.jobsToggleFilter?.value &&
        ['analysts', 'vendors', 'clients', 'facilities'].includes(currentTable)
      ) {
        showAllJobsParam = '&show_all_jobs=true';
      }

      // Pass state filter to backend (Jobs: related_state, Vendors/Facilities: state_dropdown)
      const stateFilter = query.filters.find(f => f.param === 'state' || (f.key === 'state' && (f.type === 'state_dropdown' || f.type === 'related_state')));
      const stateParam = stateFilter?.value ? `&state=${encodeURIComponent(String(stateFilter.value).trim())}` : '';

      if (hasSearchOrAdvancedFilters) {
        // Check cache first
        const currentShowAll = query.toggleFilter?.value ? true : false;
        const currentShowAllJobs = query.jobsToggleFilter?.value ? true : false;
        const cacheMatch =
          allDataCacheRef.current.table === currentTable &&
          allDataCacheRef.current.data !== null &&
          allDataCacheRef.current.showAll === currentShowAll &&
          allDataCacheRef.current.showAllJobs === currentShowAllJobs &&
          allDataCacheRef.current.stateParam === stateParam;

        if (cacheMatch) {
          const cachedData = allDataCacheRef.current.data;
          dataReceivedForTableRef.current = currentTable;
          setHartData(cachedData);
          setTotalCount(cachedData.length);
          setIsFilteredDataset(true);
          setHasReceivedData(true);
          setIsLoading(false);
          return;
        }

        // Cache miss - fetch ALL records (include inactive if toggle is on)
        const endpoint = `/hart/${backendEndpoint}?all=true${showAllParam}${showAllJobsParam}${stateParam}`;

        const response = await fetch(endpoint, { signal: abortControllerRef.current.signal });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error(`Expected JSON response but received ${contentType}`);
        }

        const data = await response.json();
        // Handle both paginated response (with results/count) and plain array response
        let allRecords = Array.isArray(data) ? data : (data.results || []);

        // Store in cache
        allDataCacheRef.current = {
          table: currentTable,
          data: allRecords,
          showAll: currentShowAll,
          showAllJobs: currentShowAllJobs,
          stateParam,
        };

        dataReceivedForTableRef.current = currentTable; // Mark that we've received data for this table
        setHartData(allRecords);
        setTotalCount(allRecords.length);
        setIsFilteredDataset(true);
        setHasReceivedData(true);
      } else {
        // No filters: fetch with pagination
        // Some endpoints may not support pagination, so we handle both formats
        const serverPage = forceServerPage !== null ? forceServerPage : Math.floor((page * rowsPerPage) / SERVER_PAGE_SIZE) + 1;
        const endpoint = `/hart/${backendEndpoint}?page=${serverPage}${showAllParam}${showAllJobsParam}${stateParam}`;

        const response = await fetch(endpoint, { signal: abortControllerRef.current.signal });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error(`Expected JSON response but received ${contentType}`);
        }

        const data = await response.json();

        // Handle both paginated response (with results/count) and plain array response
        const isArray = Array.isArray(data);
        const records = isArray ? data : (data.results || []);
        const count = isArray ? data.length : (data.count || records.length);

        setCurrentServerPage(serverPage);
        if (isArray) {
          setIsFilteredDataset(true);
        } else {
          setIsFilteredDataset(false);
        }

        // Set data after fetch completes
        dataReceivedForTableRef.current = currentTable;
        setHartData(records);
        setTotalCount(count);
        setHasReceivedData(true);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('Error fetching Hart data:', error);
      if (error.message && error.message.includes('JSON')) {
        setError(new Error(`Server returned invalid response. The endpoint may not exist or may be returning an error page.`));
      } else {
        setError(error);
      }
      setHartData([]);
      setHasReceivedData(true);
      dataReceivedForTableRef.current = currentTable;
      setIsDataReady(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset when table changes
  useEffect(() => {
    // Clear error message immediately when table changes
    setError(null);
    setCurrentServerPage(null);
    setQuery({ search: null, filters: [], toggleFilter: null, jobsToggleFilter: null });
    lastFetchedQueryRef.current = null;
    setPage(0);
    allDataCacheRef.current = { table: null, data: null, showAllJobs: null, showAll: null };
    // Reset loading states when table changes
    setIsLoading(true);
    setIsDataReady(false);
    setHasReceivedData(false);
    dataReceivedForTableRef.current = null;
    setHartData([]);
    setFilteredData([]);
    trackEvent("hart_index_viewed", { table: currentTable });
    trackEngagementActivity("hart_index_viewed");
  }, [currentTable]);

  useEffect(() => {
    if (hasSearchOrAdvancedFilters) {
      const queryChanged = JSON.stringify(lastFetchedQueryRef.current) !== JSON.stringify(query);

      if (queryChanged) {
        lastFetchedQueryRef.current = query;
        fetchHartData();
      }
    } else {
      const neededServerPage = Math.floor((page * rowsPerPage) / SERVER_PAGE_SIZE) + 1;
      const toggleChanged = JSON.stringify(lastFetchedQueryRef.current?.toggleFilter) !== JSON.stringify(query.toggleFilter);
      const jobsToggleChanged = JSON.stringify(lastFetchedQueryRef.current?.jobsToggleFilter) !== JSON.stringify(query.jobsToggleFilter);
      // User just cleared all search/filters – refetch so the list resets to full data
      const filtersCleared =
        (lastFetchedQueryRef.current?.filters?.length > 0 || lastFetchedQueryRef.current?.search) &&
        !query.filters?.length &&
        !query.search;

      if (neededServerPage !== currentServerPage || toggleChanged || jobsToggleChanged || filtersCleared) {
        lastFetchedQueryRef.current = query;
        if (filtersCleared) {
          allDataCacheRef.current = { table: null, data: null };
        }
        // Refetch data when toggle changes, page changes, or filters are cleared
        if (toggleChanged || jobsToggleChanged || neededServerPage !== currentServerPage || filtersCleared) {
          fetchHartData(filtersCleared ? 1 : neededServerPage);
        }
      }
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [currentTable, hasSearchOrAdvancedFilters, query, page, rowsPerPage, currentServerPage]);

  // Apply client-side filters and set isDataReady
  useEffect(() => {
    // Must have received data for the current table before processing
    if (!hasReceivedData || dataReceivedForTableRef.current !== currentTable) {
      setIsDataReady(false);
      return;
    }

    // Don't process if still loading
    if (isLoading) {
      setIsDataReady(false);
      return;
    }

    // Verify hartData was actually set by checking if it's an array
    if (!Array.isArray(hartData)) {
      setIsDataReady(false);
      return;
    }

    // Process data based on whether filters are active
    if (!hasSearchOrAdvancedFilters) {
      // No filters: apply toggle filter only (e.g., show only active items by default)
      const filtered = applyToggleFilter([...hartData], query.toggleFilter, currentTable);
      setFilteredData(filtered);
      setIsDataReady(true);
      return;
    }

    // With filters: need filtered dataset
    if (!isFilteredDataset) {
      // Still waiting for filtered dataset to be fetched
      setIsDataReady(false);
      return;
    }

    // We have the filtered dataset, now apply filters
    if (!hartData || hartData.length === 0) {
      // Empty dataset - set filteredData to empty and mark as ready
      setFilteredData([]);
      setIsDataReady(true);
      return;
    }

    let filtered = [...hartData];

    // Apply search filter if present
    if (query.search) {
      const searchTerms = query.search.value.split(',').map(term => term.trim().toLowerCase()).filter(term => term);
      filtered = filtered.filter(row => {
        const fieldValue = String(row[query.search.field] || '').toLowerCase();
        return searchTerms.some(term => fieldValue.includes(term));
      });
    }

    // Apply advanced filters
    query.filters.forEach(filter => {
      const { rowProperty, type, comparison, value } = filter;

      if (type === 'date') {
        const filterDate = value;

        if (comparison === '>=') {
          filtered = filtered.filter(row => {
            const rowDateStr = normalizeDateString(row[rowProperty]);
            return rowDateStr && rowDateStr >= filterDate;
          });
        } else if (comparison === '<=') {
          filtered = filtered.filter(row => {
            const rowDateStr = normalizeDateString(row[rowProperty]);
            return rowDateStr && rowDateStr <= filterDate;
          });
        }
      } else if (type === 'related_job_date') {
        // Filter by job dates on related views (analysts, vendors, clients, facilities)
        const dateField = filter.dateField;
        const filterDate = value;

        filtered = filtered.filter(row => {
          const relatedJobs = row[rowProperty];
          if (!Array.isArray(relatedJobs) || relatedJobs.length === 0) {
            return false;
          }

          // Check if any job(s) matches the date filter
          return relatedJobs.some(job => {
            const jobDateStr = normalizeDateString(job[dateField]);
            if (!jobDateStr) return false;

            if (comparison === '>=') {
              return jobDateStr >= filterDate;
            } else if (comparison === '<=') {
              return jobDateStr <= filterDate;
            }
            return false;
          });
        });
      } else if ((type === 'text' || type === 'dropdown') && comparison === 'contains') {
        filtered = filtered.filter(row =>
          String(row[rowProperty] || '').toLowerCase().includes(value.toLowerCase())
        );
      } else if (type === 'state_dropdown' && comparison === 'equals') {
        filtered = filtered.filter(row =>
          String(row[rowProperty] || '').toLowerCase() === value.toLowerCase()
        );
      } else if (type === 'related_state' && comparison === 'equals') {
        filtered = filtered.filter(row => {
          const sessions = row.sessions;
          if (!Array.isArray(sessions) || sessions.length === 0) return false;
          return sessions.some(s =>
            (s.vendor_state && s.vendor_state.toLowerCase() === value.toLowerCase()) ||
            (s.facility_state && s.facility_state.toLowerCase() === value.toLowerCase())
          );
        });
      } else if (type === 'multiselect' && comparison === 'in_array' && Array.isArray(value) && value.length > 0) {
        // For multiselect (clients, hosts, recruiters): check if any selected items match job's items
        filtered = filtered.filter(row => {
          const rowValue = row[rowProperty]; // Should be an array of objects
          if (!Array.isArray(rowValue) || rowValue.length === 0) return false;

          // Get selected IDs (convert to strings for comparison)
          const selectedIds = value.map(v => String(v.id));

          // Check if any of the job's items match any selected item
          return rowValue.some(item => {
            const itemId = item.org_id || item.vendor_id || item.id;
            return selectedIds.includes(String(itemId));
          });
        });
      }
    });

    // Apply toggle filter (status-based filtering)
    filtered = applyToggleFilter(filtered, query.toggleFilter, currentTable);

    setFilteredData(filtered);
    setPage(0);
    setIsDataReady(true);
  }, [hartData, hasSearchOrAdvancedFilters, query, isLoading, isFilteredDataset, currentServerPage, hasReceivedData, currentTable]);


  const handleDelete = async (record) => {
    let confirmMessage = '';
    let endpoint = '';
    let recordId = '';

    switch (currentTable) {
      case 'jobs':
        confirmMessage = `Are you sure you want to delete job "${record.job_name}" (${record.job_no})?`;
        endpoint = '/hart/jobs';
        recordId = record.job_no;
        break;
      case 'vendors':
        confirmMessage = `Are you sure you want to delete vendor "${record.name}"?`;
        endpoint = '/hart/vendors';
        recordId = record.vendor_id;
        break;
      case 'clients':
        confirmMessage = `Are you sure you want to delete client "${record.name}"?`;
        endpoint = '/hart/clients';
        recordId = record.org_id;
        break;
      case 'facilities':
        confirmMessage = `Are you sure you want to delete facility "${record.name}"?`;
        endpoint = '/hart/facilities';
        recordId = record.facility_id;
        break;
      default:
        return;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await deleteRecord(endpoint, recordId);
      trackEvent("hart_record_deleted", { record_type: currentTable });

      // Clear cache since data has changed
      allDataCacheRef.current = {
        table: null,
        data: null
      };

      setError(null);
      setCurrentServerPage(null);
      await fetchHartData();

    } catch (error) {
      console.error(`Error deleting ${currentTable}:`, error);
      setError(`Failed to delete ${currentTable}`);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    localStorage.setItem(ROWS_PER_PAGE_STORAGE_KEY, newRowsPerPage);
    setPage(0);
  };

  const handleChangeTableView = (newTable) => {
    setCurrentTable(newTable);
    localStorage.setItem(CURRENT_TABLE_VIEW_STORAGE_KEY, newTable);
  };

  const formatValueForExport = (value) => {
    if (value === null || value === undefined) return "";

    if (Array.isArray(value)) {
      if (value.length === 0) return "";

      const isObjectArray = typeof value[0] === "object" && value[0] !== null;
      if (isObjectArray) {
        const items = value.map(item => {
          return item.session_id || item.job_no || item.name || item.email || item.id || "";
        }).filter(Boolean);
        return items.join("; ");
      }

      return value.join("; ");
    }

    if (typeof value === "object") {
      return value.session_id || value.job_no || value.name || value.email || value.id || "";
    }

    return String(value);
  };

  const escapeCSVValue = (value) => {
    const needsEscaping = value.includes(",") || value.includes('"') || value.includes("\n");
    return needsEscaping ? `"${value.replace(/"/g, '""')}"` : value;
  };

  // Sort table data by default sort column and direction
  const sortedData = [...filteredData].sort((a, b) => {
    const sortConfig = HART_TABLES_SORT_CONFIG[currentTable];
    if (!sortConfig) return 0;

    const { column: sortColumn, direction: sortDirection } = sortConfig;

    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    // Shouldn't have null/undefined values, but just in case for any rows in the defined columns
    if (aVal === null || aVal === undefined) {
      aVal = '';
    }
    if (bVal === null || bVal === undefined) {
      bVal = '';
    }

    // For job_no, we have to sort in a special way
    // job_no has the format: INTEGER PREFIX + STRING SUFFIX ("12345ABC")
    // We want to to sort by the INTEGER first, then by the STRING if necessary
    if (sortColumn === 'job_no') {
      const aStr = String(aVal);
      const bStr = String(bVal);

      // Extract numeric and string parts with regex
      const aMatch = aStr.match(/^(\d+)(.*)$/);
      const bMatch = bStr.match(/^(\d+)(.*)$/);
      if (aMatch && bMatch) {
        const aNum = parseInt(aMatch[1]);
        const bNum = parseInt(bMatch[1]);

        // Compare numeric part
        if (aNum !== bNum) {
          if (aNum < bNum) {
            return sortDirection === 'asc' ? -1 : 1;
          } else {
            return sortDirection === 'asc' ? 1 : -1;
          }
        }

        // If numeric parts are equal, compare the string suffix
        const aSuffix = aMatch[2].toLowerCase();
        const bSuffix = bMatch[2].toLowerCase();
        if (aSuffix < bSuffix) {
          return sortDirection === 'asc' ? -1 : 1;
        } else if (aSuffix > bSuffix) {
          return sortDirection === 'asc' ? 1 : -1;
        } else {
          return 0;
        }
      }
    }

    // For other columns, treat as strings
    aVal = String(aVal).toLowerCase();
    bVal = String(bVal).toLowerCase();

    // Apply sort direction
    if (aVal < bVal) {
      return sortDirection === 'asc' ? -1 : 1;
    } else if (aVal > bVal) {
      return sortDirection === 'asc' ? 1 : -1;
    } else {
      return 0;
    }
  });

  // Need to apply filtering before pagination for accurate row count
  const displayData = (includeInactiveJobs || !['analysts', 'vendors', 'clients', 'facilities'].includes(currentTable))
    ? sortedData
    : sortedData
      .map(row => {
        const isActiveJob = (job) => {
          if (!job?.status) return true;
          return job.status.toLowerCase() === 'active';
        };
        if (currentTable === 'analysts' || currentTable === 'clients') {
          return { ...row, jobs: (row.jobs || []).filter(isActiveJob) };
        }
        if (currentTable === 'vendors' || currentTable === 'facilities') {
          return { ...row, sessions: (row.sessions || []).filter(isActiveJob) };
        }
        return row;
      })
      .filter(row => {
        // Don't display rows that don't have jobs attached
        if (currentTable === 'analysts' || currentTable === 'clients') {
          return row.jobs && row.jobs.length > 0;
        }
        if (currentTable === 'vendors' || currentTable === 'facilities') {
          return row.sessions && row.sessions.length > 0;
        }
        return true;
      });

  const paginatedData = displayData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleExport = async () => {
    try {
      setExporting(true);

      if (!sortedData || sortedData.length === 0) {
        alert("No data to export");
        return;
      }

      const tableConfig = HART_TABLES_CONFIG[currentTable];
      const headers = tableConfig.map(col => col.label).join(",");
      const rows = sortedData.map(row => {
        return tableConfig.map(col => {
          let value = row[col.field];

          // This is because we're extracting both job nos and session ids from sessions array for vendors/facilities
          if (col.field === 'sessions_jobs' && row.sessions) {
            value = row.sessions.map(s => s.job_no).filter(Boolean);
          } else if (col.field === 'sessions_ids' && row.sessions) {
            value = row.sessions.flatMap(s => s.sessions || []);
          } else if (col.field === 'vendor' && row.sessions) {
            value = row.sessions.flatMap(s => s.vendor_name || []);
          } else if (col.field === 'address') {
            value = [row.address_1, row.address_2, row.address_3].filter(Boolean).join(', ');
          } else if (col.field === 'date_range') {
            value = formatDateRange(row.start_date, row.end_date);
          }

          const formatted = formatValueForExport(value);
          return escapeCSVValue(formatted);
        }).join(",");
      });

      const csv = [headers, ...rows].join("\n");
      const cols = tableConfig.map(col => col.field);

      const timestamp = new Date().toISOString().replace(/[-:]/g, "").replace("T", "_").slice(0, 15);
      const filename = `hart_${currentTable}_export_${timestamp}.csv`;

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      try {
        await fetch("/hart/export-log/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCsrfToken(),
          },
          body: JSON.stringify({
            table_name: currentTable,
            row_count: sortedData.length,
            columns: cols,
            filters_applied: query,
          }),
        });
      } catch (logError) {
        console.error("Failed to log export:", logError);
      }

      // Note: only a boolean is sent to Mixpanel (not the raw filter/search
      // values) since Hart filters can contain client/contact names.
      const filtersApplied = Boolean(
        query?.search || query?.filters?.length || query?.toggleFilter || query?.jobsToggleFilter
      );
      trackEvent("hart_export", {
        table_name: currentTable,
        row_count: sortedData.length,
        column_count: cols.length,
        filters_applied: filtersApplied,
      });

    } catch (err) {
      console.error("Export failed:", err);
      alert("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const loggedInUser = getCurrentUser();
  const showAddButton = canEdit(projectId);
  const showExportButton = loggedInUser?.is_staff || loggedInUser?.is_superuser || canAdminister(projectId);

  return (
    <>
      <PageHeader
        title="Hart Jobs"
        action={<HeaderActions currentTable={currentTable} setCurrentTable={handleChangeTableView} showAddButton={showAddButton} />}
      />

      <BrowserTimezoneAlert
        sx={{ mb: 2, mt: 2 }}
        customMessage="All times are displayed in Eastern Time (ET)"
        cookieName="hartEasternTimezoneAlertDismissed"
      />

      {/* Search Filter Section */}
      <HartSearchFilters
        tableType={currentTable}
        onQueryChange={setQuery}
      />

      <Box component="section">
        {error ? (
          <InlineMessage
            type="error"
            message={error?.message || 'Failed to load data.'}
          />
        ) : !isDataReady || dataReceivedForTableRef.current !== currentTable ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress size={60} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading data...
            </Typography>
          </Box>
        ) : filteredData.length > 0 ? (
          <Box>
            <HartTable
              tableType={currentTable}
              data={paginatedData}
              projectId={projectId}
              onDelete={handleDelete}
            />
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderTop: '1px solid rgba(224, 224, 224, 1)',
              backgroundColor: 'white',
              borderBottomLeftRadius: '8px',
              borderBottomRightRadius: '8px',
            }}>
              {showExportButton && (
                <Button
                  onClick={handleExport}
                  variant="outlined"
                  startIcon={exporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                  disabled={exporting}
                  sx={{ ml: 2 }}
                >
                  {exporting ? "Exporting..." : "Export Current View to CSV"}
                </Button>
              )}
              {!showExportButton && <Box />}
              <TablePagination
                rowsPerPageOptions={[10, 50, 100, 250, 500]}
                component="div"
                count={
                  hasSearchOrAdvancedFilters
                    ? displayData.length
                    : sortedData.length < totalCount
                      ? totalCount
                      : displayData.length
                }
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ border: 'none' }}
              />
            </Box>
          </Box>
        ) : hasSearchOrAdvancedFilters ? (
          <InlineMessage
            type="info"
            message="No results match your filters."
          />
        ) : (
          // LAST CHECK: Only show "no data" if we've confirmed no data exists
          <InlineMessage
            type="info"
            message="No data found."
          />
        )}
      </Box>
    </>
  );
}

export default buildRoute({ element: <HartIndexPage /> });
